import { useState } from 'react';
import { useFetch } from '@/lib/fin/use-fetch';
import { useCurrency } from '@/context/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { PiggyBank } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface BudgetEntry {
  id: string; period: string; entity_id: string; entity_name: string;
  line_type: string; category_id: string | null; product_code: string | null;
  description: string | null; amount: number; currency: string;
  amount_eur: number | null; amount_usd: number | null;
  version: number; uploaded_at: string; uploaded_by: string | null;
}

interface VersionInfo { version: number; uploaded_at: string; uploaded_by: string | null; entry_count: number; }

interface BudgetData {
  entries: BudgetEntry[];
  latestVersion: number;
  versions: VersionInfo[];
}

export default function Budget() {
  const { fmt } = useCurrency();
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const { data, loading } = useFetch<BudgetData>('/api/fin/budget');

  if (loading && !data) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const version = selectedVersion ? parseInt(selectedVersion) : data?.latestVersion ?? 0;
  const filtered = (data?.entries || []).filter(e => e.version === version);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight flex items-center gap-2">
            <PiggyBank size={24} /> Budget
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Budget entries and version management</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Version:</span>
          <Select value={String(version)} onValueChange={setSelectedVersion}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(data?.versions || []).map(v => (
                <SelectItem key={v.version} value={String(v.version)}>
                  v{v.version} ({v.entry_count} entries)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Badge variant="secondary">{filtered.length} entries</Badge>
        <Badge variant="secondary">Version {version}</Badge>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Period</TableHead>
                <TableHead className="text-xs">Entity</TableHead>
                <TableHead className="text-xs">Line Type</TableHead>
                <TableHead className="text-xs">Description</TableHead>
                <TableHead className="text-xs text-right">Amount</TableHead>
                <TableHead className="text-xs">CCY</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(entry => (
                <TableRow key={entry.id}>
                  <TableCell className="text-sm">{entry.period}</TableCell>
                  <TableCell className="text-sm">{entry.entity_name}</TableCell>
                  <TableCell className="text-sm"><Badge variant="secondary" className="text-[10px]">{entry.line_type}</Badge></TableCell>
                  <TableCell className="text-sm max-w-xs truncate">{entry.description || '-'}</TableCell>
                  <TableCell className="text-sm text-right font-mono">{fmt(entry.amount, entry.currency || 'EUR')}</TableCell>
                  <TableCell className="text-sm">{entry.currency}</TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No budget entries for this version
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
